/**
 * Native Push Notifications via Capacitor
 * Mirrors the web FCM token storage schema and suppression logic from fcm.js.
 */

import { auth, db, COLLECTIONS } from '../core/firebase-config';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getUserTimezone } from '../core/timezone';
import logger from '../core/logger';

// Guard against duplicate listener registration across re-renders / auth state changes
let foregroundHandlerSetup = false;
// Stored listener handles so they can be removed before re-registering
let _foregroundListenerHandles = [];

/**
 * Register for native push notifications via Capacitor PushNotifications plugin.
 * Saves the token to Firestore fcmTokens/{uid} using the same schema as fcm.js.
 * Times out after 30 seconds.
 * @returns {Promise<string|null>} The push token or null on failure/timeout.
 */
export async function registerNativePush() {
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    const permResult = await PushNotifications.requestPermissions();
    if (permResult.receive !== 'granted') {
      logger.warn('[NativePush] Permission not granted:', permResult.receive);
      return null;
    }

    // Register with APNs — this triggers didRegisterForRemoteNotificationsWithDeviceToken
    // in AppDelegate.swift, which sets Messaging.messaging().apnsToken and kicks off the
    // APNs → FCM token exchange inside Firebase Messaging.
    await PushNotifications.register();

    // Wait for APNs registration to complete (gives us the raw APNs token).
    // We don't use this token directly — Firebase Messaging converts it to an FCM token
    // asynchronously and stores it in UserDefaults under "_cap_fcmToken" (AppDelegate.swift).
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Push registration timed out after 30s')), 30000);
      PushNotifications.addListener('registration', () => { clearTimeout(timeout); resolve(); });
      PushNotifications.addListener('registrationError', (error) => { clearTimeout(timeout); reject(error); });
    });

    // Poll for the FCM token that Firebase Messaging placed in UserDefaults (via AppDelegate).
    // Capacitor Preferences reads UserDefaults with the "_cap_" prefix, so AppDelegate stores
    // the token as "_cap_fcmToken" and we read it with key "fcmToken".
    const fcmToken = await pollForFCMToken();

    if (fcmToken) {
      await saveNativeToken(fcmToken);
      // Set pushEnabled flag — this is what Cloud Functions query to find eligible users.
      // Must be done here because requestFCMToken() (web path) returns null on native,
      // so NotificationPreferences never reaches the setDoc call otherwise.
      await setNativePushEnabled(true);
    } else {
      logger.warn('[NativePush] FCM token not available after APNs registration — AppDelegate.swift may need the Firebase Messaging setup');
    }

    return fcmToken || null;
  } catch (error) {
    logger.error('[NativePush] Registration failed:', error);
    return null;
  }
}

/**
 * Poll Capacitor Preferences (UserDefaults) for the FCM token that AppDelegate.swift
 * stores after Firebase Messaging converts the raw APNs token.
 * FCM tokens are >100 chars; raw APNs tokens are 64-char hex strings — we use length
 * to distinguish them.
 */
async function pollForFCMToken(maxAttempts = 20, intervalMs = 500) {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    for (let i = 0; i < maxAttempts; i++) {
      const { value } = await Preferences.get({ key: 'fcmToken' });
      if (value && value.length > 64) {
        return value;
      }
      await new Promise(r => setTimeout(r, intervalMs));
    }
  } catch (e) {
    logger.warn('[NativePush] Preferences not available — install @capacitor/preferences:', e.message);
  }
  return null;
}

/**
 * Save native push token to Firestore using the same schema as fcm.js saveFCMToken().
 * @param {string} token
 */
async function saveNativeToken(token) {
  const user = auth.currentUser;
  if (!user) {
    logger.warn('[NativePush] Cannot save token - no authenticated user');
    return;
  }

  try {
    const tokenRef = doc(db, COLLECTIONS.FCM_TOKENS, user.uid);
    const deviceId = token.slice(-32);

    const existing = await getDoc(tokenRef);
    const existingDevices = existing.exists() ? (existing.data().devices || {}) : {};

    const platform = getPlatformName();

    // Build the fresh device entry — explicitly omit invalidatedAt so a previously
    // server-invalidated token can be retried after the user re-registers.
    const deviceEntry = {
      token,
      active: true,
      platform,
      browser: 'Native',
      registeredAt: serverTimestamp(),
    };
    // Remove invalidatedAt from any existing entry for this deviceId
    if (existingDevices[deviceId]?.invalidatedAt) {
      delete existingDevices[deviceId].invalidatedAt;
    }

    await setDoc(tokenRef, {
      token,
      userId: user.uid,
      active: true,
      updatedAt: serverTimestamp(),
      devices: {
        ...existingDevices,
        [deviceId]: deviceEntry,
      }
    }, { merge: true });

    logger.info('[NativePush] Token saved to Firestore', { deviceId });
  } catch (error) {
    logger.error('[NativePush] Failed to save token:', error);
  }
}

/**
 * Set pushEnabled in notificationPreferences after successful native token registration.
 * Also stores the user's timezone so Cloud Functions can apply correct quiet hours.
 */
async function setNativePushEnabled(enabled) {
  const user = auth.currentUser;
  if (!user) return;
  try {
    await setDoc(doc(db, COLLECTIONS.NOTIFICATION_PREFERENCES, user.uid), {
      pushEnabled: enabled,
      ...(enabled ? { timezone: getUserTimezone() } : {}),
    }, { merge: true });
    logger.info('[NativePush] pushEnabled set to', enabled);
  } catch (error) {
    logger.error('[NativePush] Failed to set pushEnabled:', error);
  }
}

/**
 * Disable native push — marks all active device tokens inactive and clears the flag.
 */
export async function disableNativePush() {
  const user = auth.currentUser;
  if (!user) return;
  try {
    const tokenRef = doc(db, COLLECTIONS.FCM_TOKENS, user.uid);
    const snap = await getDoc(tokenRef);
    if (snap.exists()) {
      const devices = snap.data().devices || {};
      const updates = {};
      for (const [deviceId, device] of Object.entries(devices)) {
        if (device.active) {
          updates[`devices.${deviceId}.active`] = false;
        }
      }
      if (Object.keys(updates).length > 0) {
        await updateDoc(tokenRef, updates);
      }
    }
    await setNativePushEnabled(false);
    logger.info('[NativePush] Push disabled');
  } catch (error) {
    logger.error('[NativePush] Failed to disable push:', error);
  }
}

/**
 * Set up foreground push notification handler for native.
 * Mirrors fcm.js handleForegroundMessage suppression logic.
 * Removes any previously registered listeners before re-registering so they
 * don't accumulate across auth state changes or component remounts.
 */
export async function setupNativeForegroundHandler() {
  // Remove stale listeners from any previous call before re-registering
  if (_foregroundListenerHandles.length > 0) {
    for (const handle of _foregroundListenerHandles) {
      try { handle.remove(); } catch { /* ignore */ }
    }
    _foregroundListenerHandles = [];
    foregroundHandlerSetup = false;
  }

  if (foregroundHandlerSetup) return;
  foregroundHandlerSetup = true;

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    // Foreground: notification arrived while app is open
    const h1 = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      const body = notification.body || notification.data?.body;
      if (!body) return;

      // Suppress insight toasts for 60s after user saves an entry (same as web)
      // Guarded: WKWebView with ITP can throw on sessionStorage access.
      let savedAtRaw = '0';
      try { savedAtRaw = sessionStorage.getItem('clarity_last_entry_saved') || '0'; } catch { /* ignore */ }
      const savedAt = parseInt(savedAtRaw, 10);
      if (Date.now() - savedAt < 60000) {
        return;
      }

      logger.info('[NativePush] Foreground notification:', body);
    });
    _foregroundListenerHandles.push(h1);

    // Background/quit: user tapped the notification — navigate to the right screen
    const h2 = await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification?.data || {};
      const url = data.url || data.link;

      if (url) {
        // Dispatch the same event that fcm.js uses on web so the router handles it.
        // If the layout hasn't mounted yet (cold start), also queue it so the layout
        // can drain it on mount. The timestamp lets the layout discard stale queues.
        window.__clarityPendingNav = { url, ts: Date.now() };
        window.dispatchEvent(new CustomEvent('clarity-navigate', { detail: url }));
        logger.info('[NativePush] Notification tapped, navigating to:', url);
      } else {
        logger.info('[NativePush] Notification tapped, no url in payload');
      }
    });
    _foregroundListenerHandles.push(h2);
  } catch (error) {
    logger.error('[NativePush] Failed to set up foreground handler:', error);
    foregroundHandlerSetup = false;
  }
}

/**
 * Get platform name for native context.
 * @returns {string}
 */
function getPlatformName() {
  if (typeof window !== 'undefined' && window.Capacitor) {
    const platform = window.Capacitor.getPlatform();
    if (platform === 'ios') return 'iOS';
    if (platform === 'android') return 'Android';
  }
  return 'Native';
}
